import {Component, effect, inject, input, signal} from '@angular/core';
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {LineChartModule} from "@swimlane/ngx-charts";
import {Location, NgClass} from "@angular/common";
import {BluetoothSvgComponent, Status} from "src/app/presentation/common/icons/bluetooth-svg/bluetooth-svg.component";
import {isEqualToOrOlderThanOffset, isEqualToOrYoungerThanOffset} from "src/app/app.config";
import {Animal, DeviceAssociation, FixedSamplePoint} from "src/app/infrastructure/moovement-api/models/asset";
import {fifteenMinutes, fiveDays, oneHour, twentyFourHours} from "src/app/presentation/common/globals";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {AggregatedDeviceStateStore} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.store';
import {AggregatedDeviceState} from 'src/app/domain/devices/aggregated-state/aggregated-device-state';
import {InterDeviceAssociationType} from 'src/app/domain/devices/inter-device-association';
import {DataType} from 'src/app/domain/domain-models';
import {BootstrapService} from 'src/app/infrastructure/init/bootstrap.service';
import {MapStateService} from 'src/app/presentation/main-view/map-container/map-container-component';
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {
  ObservedAnimalsComponent
} from 'src/app/presentation/main-view/information-center/proximity-sample-points/proximity-sample-point-details/observed-animals/observed-animals.component';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {timeAgo} from 'src/app/domain/util/timestamps/timestamps';
import {FixedSamplePointsStore} from 'src/app/domain/assets/fixed-sample-points/fixed-sample-points.store';
import {
  ScannerComponent
} from "src/app/presentation/main-view/information-center/proximity-sample-points/proximity-sample-point-details/scanner/scanner.component";
import {isBluetoothProximitySensor} from 'src/app/domain/devices/bluetooth-proximity-sensor/bluetooth-proximity-sensor';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {AnimalSelectionStore} from 'src/app/domain/assets/animals/animal-selection.store';
import {
  Observation
} from 'src/app/presentation/main-view/information-center/proximity-sample-points/proximity-sample-point-details/observation';
import {AggregatedDeviceStateService} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.service';
import {AggregatedAssetStateService} from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.service';
import {Router} from '@angular/router';
import {AggregatedAssetStateStore} from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {UserStore} from 'src/app/domain/user/user.store';
import {AssetMetadataComponent} from "src/app/presentation/common/asset-metadata/asset-metadata.component";

export const proximityPointMidVoltageThreshold = 3.91
export const proximityPointLowVoltageThreshold = 2.00

@Component({
  selector: 'app-proximity-sample-point-details',
  imports: [
    LineChartModule,
    NgClass,
    BluetoothSvgComponent,
    GenericButtonComponent,
    ObservedAnimalsComponent,
    ScannerComponent,
    AssetMetadataComponent
  ],
  templateUrl: './proximity-sample-point-details.component.html',
  styleUrl: './proximity-sample-point-details.component.css'
})
export class ProximitySamplePointDetailsComponent {

  id = input.required<string>();

  loading = false;
  canBeAltered: boolean = false;
  canBeDeleted: boolean = false;

  asset = signal<FixedSamplePoint | undefined>(undefined);
  deviceAssociations = signal<DeviceAssociation[]>([]);

  assignedAnimals = signal<Set<Animal>>(new Set());

  associatedScannerIds = signal<Set<string>>(new Set());

  currentlyVisibleAnimals = signal<Animal[]>([]);
  animalsSeenInLastHour = signal<Animal[]>([]);
  animalsSeenInLastTwentyFourHours = signal<Animal[]>([]);
  animalsSeenInLastFiveDays = signal<Animal[]>([]);
  animalsSeenAtLeastFiveDaysAgo = signal<Animal[]>([]);
  animalsAssignedButNotSeenLastHour = signal<Animal[]>([]);
  animalsAssignedButNotSeenLastTwentyFourHours = signal<Animal[]>([]);
  animalsAssignedButNeverSeen = signal<Animal[]>([]);

  lastObservationPerDeviceId = signal<Map<string, Observation>>(new Map());
  lastObservationPerAnimalId = signal<Map<string, Observation>>(new Map());

  status = signal(Status.OFFLINE);
  statusMessage = signal('Last seen: ?');
  statusMessageColorClass = signal('color-danger');

  hasSampleBasedLocation = signal(false);

  currentLocation = signal<number[] | undefined>(undefined);

  private currentlyInRangeThreshold = signal(new Date(Date.now() - fifteenMinutes));

  private location = inject(Location);
  private router = inject(Router);

  private animalSelectionStore = inject(AnimalSelectionStore);
  private animalStore = inject(AnimalsStore);
  private aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  private aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  private deviceAssociationStore = inject(DeviceAssociationStore);
  private deviceStore = inject(DeviceStore);
  private fixedSamplePointsStore = inject(FixedSamplePointsStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private mainViewStore = inject(MainViewStore);
  private userStore = inject(UserStore);

  constructor(
    protected viewportService: ViewportService,
    private dataSynchronisationService: DataSynchronisationService,
    private dataSubscriptionService: DataSubscriptionService,
    private aggregatedAssetStateService: AggregatedAssetStateService,
    private aggregatedDeviceStateService: AggregatedDeviceStateService,
    private bootstrapService: BootstrapService,
    private mapStateService: MapStateService
  ) {
    this.animalSelectionStore.reset();

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      this.dataSubscriptionService.publish(assetId + '.focus', randomUuid());

      const asset = this.fixedSamplePointsStore.entityMap()[assetId];
      if (!asset) return;

      this.asset.set(asset);
      this.hasSampleBasedLocation.set(asset.sampleBasedLocation);
    });

    effect(() => {
      const relatedDeviceAssociations = this.deviceAssociationStore.assetIdToAssocations().get(this.id()) || [];
      this.deviceAssociations.set(relatedDeviceAssociations);
    });

    effect(() => {
      const deviceAssociations = this.deviceAssociations();
      const deviceStates = deviceAssociations
        .map(deviceAssociation => this.aggregatedDeviceStateStore.entityMap()[deviceAssociation.deviceId])
        .filter(state => state !== undefined);

      const associatedDeviceIds = deviceStates
        .flatMap(deviceState => deviceState.associatedDevices.get(InterDeviceAssociationType.BLUETOOTH_SCANNER_ASSIGNMENT))
        .filter(deviceId => deviceId != undefined);

      this.lastObservationPerDeviceId.set(this.toLastObservationPerDevice(deviceStates));

      const associatedAnimals = associatedDeviceIds
        .map(deviceId => this.animalStore.deviceIdToAnimal().get(deviceId))
        .filter(animal => animal !== undefined);

      this.assignedAnimals.set(new Set(associatedAnimals));

      this.currentlyVisibleAnimals.set(this.getAnimals(this.lastObservationPerDeviceId(), (date: Date) => date >= this.currentlyInRangeThreshold()));
      this.animalsSeenInLastHour.set(this.getAnimals(this.lastObservationPerDeviceId(), (date: Date) => isEqualToOrYoungerThanOffset(date, oneHour)));
      this.animalsSeenInLastTwentyFourHours.set(this.getAnimals(this.lastObservationPerDeviceId(), (date: Date) => isEqualToOrYoungerThanOffset(date, twentyFourHours)));
      this.animalsSeenInLastFiveDays.set(this.getAnimals(this.lastObservationPerDeviceId(), (date: Date) => isEqualToOrYoungerThanOffset(date, fiveDays)));
      this.animalsSeenAtLeastFiveDaysAgo.set(this.getAnimals(this.lastObservationPerDeviceId(), (date: Date) => isEqualToOrOlderThanOffset(date, fiveDays)));
      this.animalsAssignedButNotSeenLastHour.set(this.assignedButNotSeen(this.lastObservationPerDeviceId(), associatedDeviceIds, oneHour));
      this.animalsAssignedButNotSeenLastTwentyFourHours.set(this.assignedButNotSeen(this.lastObservationPerDeviceId(), associatedDeviceIds, twentyFourHours));
      this.animalsAssignedButNeverSeen.set(this.assignedButNeverSeen(this.lastObservationPerDeviceId(), associatedDeviceIds));
    });

    effect(() => {
      const result = new Map<string, Observation>();
      for (const [deviceId, observation] of this.lastObservationPerDeviceId()) {
        const animal = this.animalStore.deviceIdToAnimal().get(deviceId);
        if (!animal) continue;

        result.set(animal.id, observation);
      }

      this.lastObservationPerAnimalId.set(result);
    });

    effect(() => {
      const aggregatedState = this.aggregatedAssetStateStore.entityMap()[this.id()];
      if (!aggregatedState) return;

      const lastSeenByGatewayAt = aggregatedState.lastSeenByGatewayAt;
      this.statusMessage.set(`Last seen: ${timeAgo(lastSeenByGatewayAt)}`);
      const isSeenWithinThreshold = lastSeenByGatewayAt !== undefined && isEqualToOrYoungerThanOffset(lastSeenByGatewayAt, oneHour);
      if (isSeenWithinThreshold) {
        this.statusMessageColorClass.set("color-primary-information");
        this.status.set(Status.ONLINE);
      } else {
        this.statusMessageColorClass.set("color-danger");
        this.status.set(Status.OFFLINE);
      }

      const position = aggregatedState.position;
      this.currentLocation.set(position);
    });

    effect(() => {
      const associations = this.deviceAssociations();
      const scannerIds = associations
        .map(association => association.deviceId)
        .map(deviceId => this.deviceStore.entityMap()[deviceId])
        .filter(device => device != undefined)
        .filter(device => isBluetoothProximitySensor(device))
        .map(device => device.id);
      this.associatedScannerIds.set(new Set(scannerIds));
    });

    effect(() => {
      this.canBeAltered = this.userStore.canEditPersistable(this.asset());
      this.canBeDeleted = this.userStore.canDeletePersistable(this.asset());
    });
  }

  private toLastObservationPerDevice(aggregatedStates: AggregatedDeviceState[]) {
    const result = new Map<string, Observation>();
    for (const aggregatedState of aggregatedStates) {
      const lastObservationPerDevice = aggregatedState.lastObservationPerDevice;
      if (!lastObservationPerDevice) continue;

      for (const [deviceId, instant] of lastObservationPerDevice) {
        const current = result.get(deviceId);
        if (!current || current.instant < instant) result.set(deviceId, {id: aggregatedState.id, instant});
      }
    }

    return result;
  }

  private getAnimals(lastObservationPerDevice: Map<string, Observation>, filter: (date: Date) => boolean) {
    const result: Animal[] = [];

    lastObservationPerDevice.forEach((observation, deviceId) => {
      if (!filter(observation.instant)) return;

      const animal = this.animalStore.deviceIdToAnimal().get(deviceId);
      if (!animal) return;

      result.push(animal);
    });

    return result;
  }

  private assignedButNeverSeen(lastObservationPerDevice: Map<string, Observation>, assignedDeviceIds: string[]) {
    if (assignedDeviceIds.length === 0) return [];

    const seenDeviceIds = new Set(lastObservationPerDevice.keys());

    const assignedButNeverSeenDeviceIds = new Set(assignedDeviceIds);
    seenDeviceIds.forEach(deviceId => assignedButNeverSeenDeviceIds.delete(deviceId));

    return this.animalsForDeviceIds(assignedButNeverSeenDeviceIds);
  }

  private assignedButNotSeen(lastObservationPerDevice: Map<string, Observation>, assignedDeviceIds: string[], offset: number) {
    if (assignedDeviceIds.length === 0) return [];

    const seenDeviceIds = new Set(lastObservationPerDevice.keys());

    const threshold = new Date(Date.now() - offset);
    const assignedButNotSeenDeviceIds = new Set(assignedDeviceIds);
    seenDeviceIds.forEach(deviceId => {
      const observation = lastObservationPerDevice.get(deviceId);
      if (observation && observation.instant >= threshold) assignedButNotSeenDeviceIds.delete(deviceId);
    });

    return this.animalsForDeviceIds(assignedButNotSeenDeviceIds);
  }

  private animalsForDeviceIds(deviceIds: Set<string>) {
    const result: Animal[] = [];
    for (const deviceId of deviceIds) {
      const animal = this.animalStore.deviceIdToAnimal().get(deviceId);
      if (animal) result.push(animal);
    }

    return result;
  }

  async delete() {
    const asset = this.asset();
    if (!asset) return;

    if (confirm(`Are you sure you want to delete scanner '${asset.name}'?`)) {
      this.loading = true;
      try {
        await this.dataSynchronisationService.dissociateAsset(asset, DataType.proximitySamplePoints);
        this.bootstrapService.reloadFixedAssetSamplePoints();
        this.internalNotificationsStore.addNotification("Successfully deleted BLE scanner.", InternalNotificationType.SUCCESS, false);
        this.loading = false;
        this.mapStateService.removeLocator();
        this.router.navigate(['/main/information-center']);
      } catch (error) {
        this.internalNotificationsStore.addNotification("Unable to delete BLE scanner.", InternalNotificationType.ERROR, false);
        this.loading = false;
      }
    }
  }

  customizeDevice(deviceId: string) {
    this.router.navigate(['/main/customize-bluetooth-proximity-sensor', deviceId]);
  }

  resetCurrentlyInRangeThreshold() {
    this.currentlyInRangeThreshold.set(new Date());
  }

  refresh() {
    const deviceIds = this.deviceAssociations().map(association => association.deviceId);
    this.aggregatedDeviceStateService.reloadAggregatedDeviceStates(deviceIds).subscribe();

    const assetId = this.id();
    this.aggregatedAssetStateService.reloadAggregatedAssetState(assetId).subscribe();
  }

  toggleCustomize(url: string): void {
    if (!this.loading && this.canBeAltered) {
      this.router.navigate([url, this.id()]);
    }
  }

  showLocation() {
    const location = this.currentLocation();
    if (!location) return;

    this.mapStateService.moveLocator(location[0], location[1]);
    this.mapStateService.centerPan(location[0], location[1]);
  }

  protected close() {
    this.mainViewStore.hideChildComponent();
  }

  protected back(): void {
    this.location.back();
  }
}
